import FolderInterface from '../../graphql/Folders/FolderInterface';
import { FolderSchema } from '../../types/Folder';

const inFlightFetches = new Map<string, Promise<FolderSchema>>();

/**
 * Fetches a folder by id, sharing a single in-flight request across
 * concurrent callers for the same folder id (+ contactId, for the read-only/
 * contact viewing path). Without this, `useFolderData` (fired when a folder
 * is navigated into) and `applyFolderTargetedContents` (fired when content is
 * targeted at a folder before it's been fetched) can each independently call
 * `FolderInterface().getFolder` for the SAME folder at the same time, wasting
 * a full network round-trip — deduping the *results* of two separate fetches
 * (which both callers already do) doesn't avoid making the second request.
 *
 * The cache entry lives only for the duration of the request: once the
 * promise settles (success or failure) it's removed, so a later, genuinely
 * new fetch for the same folder id is never served stale data.
 */
export const fetchFolderShared = (
  folderId: string,
  contactId?: string
): Promise<FolderSchema> => {
  const cacheKey = contactId ? `${folderId}:${contactId}` : folderId;
  const existing = inFlightFetches.get(cacheKey);
  if (existing) return existing;

  const promise = new FolderInterface(contactId)
    .getFolder(folderId)
    .finally(() => {
      inFlightFetches.delete(cacheKey);
    });

  inFlightFetches.set(cacheKey, promise);
  return promise;
};
